import { catalogv1 as api } from '@/adapters';
import type {
  Account as AccountModel,
  EntityKindSummary,
  EntityPageAccount,
  ActivityLogPage,
  EntityPageGroup,
  EntityPageLicense,
  EntityPagePermission,
  EntityPagePerson,
  EntityPageRole,
  EntityPageTicket,
  EntityPageTicketComment,
  Group as GroupModel,
  License as LicenseModel,
  Permission as PermissionModel,
  Person as PersonModel,
  Role as RoleModel,
  TicketComment as TicketCommentModel,
  Ticket as TicketModel,
} from '@/adapters/catalogv1.g';
import {
  extractAccount,
  extractCatalogRole,
  extractGroup,
  extractLicense,
  extractPermission,
  extractPerson,
  extractTicket,
  extractTicketComment,
  unwrapData,
} from '@/adapters/shared';
import {
  type CatalogLogEnvelope,
  extractLogEvent,
} from '@/adapters/shared/catalog/extract-log-event';
import { catalogListActivityLogsFailureError } from '@/services/catalog-logs-errors';
import type {
  CatalogAccount,
  CatalogGroup,
  CatalogLicense,
  CatalogPermission,
  CatalogPerson,
  CatalogRole,
  CatalogTicket,
  CatalogTicketComment,
} from '@/adapters/shared/catalog';
import type { LogEvent, LogEventFilter } from '@/types/data/catalog-log-event';
import { eventDiscriminator } from '@/types/data/catalog-log-helpers';
import type { Page, PageRequest } from '@/types/shared/page';
import { toApiQuery } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import {
  buildListCatalogLogsQuery,
  MAX_CATALOG_LOG_CONNECTOR_IDS,
  MAX_CATALOG_LOG_PAGE_LIMIT,
} from '@/services/catalog-logs-query';

/**
 * Extract models from a paginated response
 */
function extractPageModels<T>(
  page: { models?: T[]; next?: string; prev?: string } | null | undefined
): T[] {
  return page?.models ?? [];
}

/**
 * Client-side discriminator filter (defense-in-depth). The catalog logs API
 * already filters via `kinds`; list calls send `filter.discriminators` as
 * `kinds` server-side, and this narrows again if the envelope set is wider.
 */

function filterLogEvents(
  events: LogEvent[],
  filter?: LogEventFilter
): LogEvent[] {
  if (!filter?.discriminators?.length) {
    return events;
  }
  const allowed = new Set<string>(filter.discriminators);
  return events.filter((event) => allowed.has(eventDiscriminator(event)));
}

export const catalogService = {
  // ==================== ACCOUNTS ====================
  listAccounts: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogAccount>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listAccounts(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageAccount>(res);
    const models = extractPageModels<AccountModel>(page);
    return {
      items: models.map(extractAccount),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findAccount: async (
    tenantId: UUID,
    connectorId: UUID,
    accountId: UUID
  ): Promise<CatalogAccount | null> => {
    const res = await api.findAccount(tenantId, connectorId, accountId);
    const model = unwrapData<AccountModel>(res);
    if (!model) return null;
    return extractAccount(model);
  },

  // ==================== PERSONS ====================
  listPersons: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogPerson>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listPersons(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPagePerson>(res);
    const models = extractPageModels<PersonModel>(page);
    return {
      items: models.map(extractPerson),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findPerson: async (
    tenantId: UUID,
    connectorId: UUID,
    personId: UUID
  ): Promise<CatalogPerson | null> => {
    const res = await api.findPerson(tenantId, connectorId, personId);
    const model = unwrapData<PersonModel>(res);
    if (!model) return null;
    return extractPerson(model);
  },

  // ==================== GROUPS ====================
  listGroups: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogGroup>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listGroups(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageGroup>(res);
    const models = extractPageModels<GroupModel>(page);
    return {
      items: models.map(extractGroup),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findGroup: async (
    tenantId: UUID,
    connectorId: UUID,
    groupId: UUID
  ): Promise<CatalogGroup | null> => {
    const res = await api.findGroup(tenantId, connectorId, groupId);
    const model = unwrapData<GroupModel>(res);
    if (!model) return null;
    return extractGroup(model);
  },

  // ==================== ROLES ====================
  listRoles: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogRole>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listRoles(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageRole>(res);
    const models = extractPageModels<RoleModel>(page);
    return {
      items: models.map(extractCatalogRole),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findRole: async (
    tenantId: UUID,
    connectorId: UUID,
    roleId: UUID
  ): Promise<CatalogRole | null> => {
    const res = await api.findRole(tenantId, connectorId, roleId);
    const model = unwrapData<RoleModel>(res);
    if (!model) return null;
    return extractCatalogRole(model);
  },

  // ==================== LICENSES ====================
  listLicenses: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogLicense>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listLicenses(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageLicense>(res);
    const models = extractPageModels<LicenseModel>(page);
    return {
      items: models.map(extractLicense),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findLicense: async (
    tenantId: UUID,
    connectorId: UUID,
    licenseId: UUID
  ): Promise<CatalogLicense | null> => {
    const res = await api.findLicense(tenantId, connectorId, licenseId);
    const model = unwrapData<LicenseModel>(res);
    if (!model) return null;
    return extractLicense(model);
  },

  // ==================== PERMISSIONS ====================
  listPermissions: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogPermission>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listPermissions(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPagePermission>(res);
    const models = extractPageModels<PermissionModel>(page);
    return {
      items: models.map(extractPermission),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findPermission: async (
    tenantId: UUID,
    connectorId: UUID,
    permissionId: UUID
  ): Promise<CatalogPermission | null> => {
    const res = await api.findPermission(tenantId, connectorId, permissionId);
    const model = unwrapData<PermissionModel>(res);
    if (!model) return null;
    return extractPermission(model);
  },

  // ==================== ENTITY KINDS ====================
  listEntities: async (
    tenantId: UUID,
    connectorId: UUID
  ): Promise<EntityKindSummary[]> => {
    const res = await api.listEntityKinds(tenantId, connectorId);
    if (!res.ok) {
      throw new Error(
        `Failed to load catalog entities (${String(res.status)})${
          res.error?.message ? `: ${res.error.message}` : ''
        }`
      );
    }
    // A 200 with no body leaves `res.data` null/undefined at runtime despite
    // its type. That is an incomplete response, not an empty catalog — treat it
    // as a failure so the query retries instead of caching a bogus empty list.
    // A genuinely empty catalog returns `[]`, which passes this check.
    if (!Array.isArray(res.data)) {
      throw new Error(
        `Catalog entities response was empty (status ${String(res.status)} with no body)`
      );
    }
    return res.data;
  },

  // ==================== TICKETS ====================
  listTickets: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogTicket>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listTickets(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageTicket>(res);
    const models = extractPageModels<TicketModel>(page);
    return {
      items: models.map(extractTicket),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findTicket: async (
    tenantId: UUID,
    connectorId: UUID,
    ticketId: UUID
  ): Promise<CatalogTicket | null> => {
    const res = await api.findTicket(tenantId, connectorId, ticketId);
    const model = unwrapData<TicketModel>(res);
    if (!model) return null;
    return extractTicket(model);
  },

  // ==================== TICKET COMMENTS ====================
  listTicketComments: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest
  ): Promise<Page<CatalogTicketComment>> => {
    const apiQuery = toApiQuery(query);
    const res = await api.listTicketComments(tenantId, connectorId, apiQuery);
    const page = unwrapData<EntityPageTicketComment>(res);
    const models = extractPageModels<TicketCommentModel>(page);
    return {
      items: models.map(extractTicketComment),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  findTicketComment: async (
    tenantId: UUID,
    connectorId: UUID,
    ticketCommentId: UUID
  ): Promise<CatalogTicketComment | null> => {
    const res = await api.findTicketComment(
      tenantId,
      connectorId,
      ticketCommentId
    );
    const model = unwrapData<TicketCommentModel>(res);
    if (!model) return null;
    return extractTicketComment(model);
  },

  // ==================== LOG EVENTS ====================
  listConnectorLogEvents: async (
    tenantId: UUID,
    connectorId: UUID,
    query?: PageRequest,
    filter?: LogEventFilter
  ): Promise<Page<LogEvent>> => {
    const apiQuery = buildListCatalogLogsQuery(
      { limit: query?.limit, next: query?.next },
      filter,
      { connectorIds: [String(connectorId)] }
    );
    const res = await api.listActivityLogs(tenantId, apiQuery);
    if (!res.ok) {
      throw catalogListActivityLogsFailureError(
        res.error?.message,
        `Failed to load connector logs (${String(res.status)})`
      );
    }
    const page = unwrapData<ActivityLogPage>(res);
    const models = extractPageModels<CatalogLogEnvelope>(page);
    const events = models.map(extractLogEvent);
    return {
      items: filterLogEvents(events, filter),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  listTenantLogEvents: async (
    tenantId: UUID,
    query?: PageRequest,
    filter?: LogEventFilter
  ): Promise<Page<LogEvent>> => {
    if (
      filter?.connectorIds &&
      filter.connectorIds.length > MAX_CATALOG_LOG_CONNECTOR_IDS
    ) {
      throw new Error(
        `Select at most ${String(MAX_CATALOG_LOG_CONNECTOR_IDS)} connectors for merged logs.`
      );
    }
    const apiQuery = buildListCatalogLogsQuery(
      { limit: query?.limit, next: query?.next },
      filter
    );
    const res = await api.listActivityLogs(tenantId, apiQuery);
    if (!res.ok) {
      throw catalogListActivityLogsFailureError(
        res.error?.message,
        `Failed to load tenant logs (${String(res.status)})`
      );
    }
    const page = unwrapData<ActivityLogPage>(res);
    const models = extractPageModels<CatalogLogEnvelope>(page);
    const events = models.map(extractLogEvent);
    return {
      items: filterLogEvents(events, filter),
      next: page?.next ?? null,
      prev: null,
      limit: query?.limit,
    };
  },

  // ==================== ENTITY EVENT HISTORY ====================

  /**
   * Log events for a specific catalog target ref (newest first, bounded by
   * catalog retention). Uses `GET .../logs` with `target_ref`.
   */
  listEntityEventHistory: async (
    tenantId: UUID,
    targetRef: string
  ): Promise<Page<LogEvent>> => {
    const apiQuery = buildListCatalogLogsQuery(
      { limit: MAX_CATALOG_LOG_PAGE_LIMIT },
      undefined,
      { targetRef: targetRef }
    );
    const res = await api.listActivityLogs(tenantId, apiQuery);
    if (!res.ok) {
      throw catalogListActivityLogsFailureError(
        res.error?.message,
        `Failed to load entity log history (${String(res.status)})`
      );
    }
    const page = unwrapData<ActivityLogPage>(res);
    const models = extractPageModels<CatalogLogEnvelope>(page);
    const events = models.map(extractLogEvent);
    return {
      items: events,
      next: page?.next ?? null,
      prev: null,
      limit: MAX_CATALOG_LOG_PAGE_LIMIT,
    };
  },

  // ==================== ACCOUNT RECONCILIATION ====================
  /** Reconcile (create/update) an account without needing the internal ID */
  reconcileAccount: async (
    tenantId: UUID,
    connectorId: UUID,
    account: AccountModel
  ): Promise<boolean> => {
    const res = await api.reconcileAccountWithoutID(
      tenantId,
      connectorId,
      account
    );
    // The API returns EntityMetadata on success
    return res.ok === true;
  },
};
